import {
  Delete as DeleteIcon,
  Folder as FolderIcon,
  FolderSpecial as FolderSpecialIcon,
  Home as HomeIcon,
  LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useAccount } from '../../contexts/AccountContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { listFolders } from '../../services/api';
import { BaseModal } from '../common/UI/BaseModal';

interface FolderBrowserModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (path: string) => void;
  siteId?: string | undefined;
  driveId?: string | undefined;
  mode?: string;
  existingPaths?: string[];
  onRemovePath?: (path: string) => void;
}

const FolderBrowserModal: React.FC<FolderBrowserModalProps> = ({
  open,
  onClose,
  onSelect,
  siteId,
  driveId,
  mode,
  existingPaths,
  onRemovePath,
}) => {
  const { account } = useAccount();
  const { showSnackbar } = useSnackbar();
  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [pathToRemove, setPathToRemove] = useState<string | null>(null);

  const handleSelectClick = async () => {
    setIsSelecting(true);
    try {
      await onSelect(currentPath);
    } finally {
      setIsSelecting(false);
    }
  };

  const handleRemoveConfirm = () => {
    if (pathToRemove && onRemovePath) {
      onRemovePath(pathToRemove);
    }
    setPathToRemove(null);
  };

  const loadFolders = useCallback(
    async (_path: string) => {
      if (!account?.uid || !account?.clientId) {
        return;
      }
      setLoading(true);
      try {
        const payload: {
          userId: string;
          clientId: string;
          path: string;
          siteId?: string;
          driveId?: string;
          mode?: string;
        } = {
          userId: account.uid,
          clientId: account.clientId,
          path: _path,
          ...(mode ? { mode } : {}),
        };

        if (siteId) {
          payload.siteId = siteId;
        }
        if (driveId) {
          payload.driveId = driveId;
        }

        const response = (await listFolders(payload)) as {
          success: boolean;
          folders?: string[];
          message?: string;
        };

        if (response.success && response.folders) {
          setFolders(response.folders);
        } else {
          if (response.success && !response.folders) {
            setFolders([]);
          } else if (!response.success) {
            showSnackbar(`Failed to list folders: ${response.message || 'Unknown error'}`, 'error');
          }
        }
      } catch (_error) {
        showSnackbar('Error listing folders', 'error');
      } finally {
        setLoading(false);
      }
    },
    [account, showSnackbar, siteId, driveId, mode]
  );

  useEffect(() => {
    if (open) {
      loadFolders(currentPath);
    }
  }, [open, currentPath, loadFolders]);

  const handleNavigate = (folderName: string) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    setCurrentPath(newPath);
  };

  const handleBreadcrumbClick = (index: number, parts: string[]) => {
    const newPath = parts.slice(0, index + 1).join('/');
    setCurrentPath(newPath);
  };

  const isAlreadyConnected = existingPaths?.includes(currentPath) || false;

  return (
    <>
      <BaseModal
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        title="Manage Folders"
        actions={
          <>
            <Button onClick={onClose}>Close</Button>
            <Button
              variant="contained"
              onClick={handleSelectClick}
              disabled={!currentPath || isSelecting || isAlreadyConnected}
            >
              {isSelecting ? (
                <CircularProgress size={24} color="inherit" />
              ) : isAlreadyConnected ? (
                `Already connected to this category`
              ) : (
                `Select "${currentPath || 'Root'}"`
              )}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, py: 1 }}>
          {/* Connected Folders Section */}
          {existingPaths && existingPaths.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}
              >
                Currently Connected Folders
              </Typography>
              <List
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {existingPaths.map((path) => (
                  <ListItem
                    key={path}
                    divider
                    secondaryAction={
                      onRemovePath ? (
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => setPathToRemove(path)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      ) : null
                    }
                  >
                    <ListItemIcon>
                      <FolderSpecialIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={path || 'Root Folder'} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Directory Browser Section */}
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}
            >
              Browse Directory
            </Typography>

            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
              <Link
                underline="hover"
                color="inherit"
                onClick={() => setCurrentPath('')}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              >
                <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                Root
              </Link>
              {currentPath
                .split('/')
                .filter((p) => p)
                .map((part, index, arr) => (
                  <Link
                    key={part}
                    underline="hover"
                    color="inherit"
                    onClick={() => handleBreadcrumbClick(index, arr)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {part}
                  </Link>
                ))}
            </Breadcrumbs>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  minHeight: 200,
                  maxHeight: 400,
                  overflow: 'auto',
                }}
              >
                {folders.length === 0 && (
                  <Typography color="text.secondary" sx={{ p: 2 }}>
                    No subfolders found.
                  </Typography>
                )}
                {folders.map((folder) => (
                  <ListItem key={folder} disablePadding divider>
                    <ListItemButton onClick={() => handleNavigate(folder)}>
                      <ListItemIcon>
                        <FolderIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={folder} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </BaseModal>

      {/* Removal Confirmation Dialog */}
      <Dialog open={Boolean(pathToRemove)} onClose={() => setPathToRemove(null)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkOffIcon color="error" /> Remove Folder Connection?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove the connection to{' '}
            <strong>{pathToRemove || 'Root Folder'}</strong>? This will stop syncing documents from
            this folder, and any previously synced documents will be removed during the next sync.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setPathToRemove(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemoveConfirm}>
            Remove Connection
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FolderBrowserModal;
